import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { useParams } from 'react-router-dom';
import { reportingApi } from '../../services/api';
import { getReportMeta } from './config/reportRegistry';
import { useNotifications } from '../../context/NotificationContext';
import ReportParameterCombobox from './components/ReportParameterCombobox';
import ReportToolbar from './components/ReportToolbar';
import ReportPaginator from './components/ReportPaginator';
import { ArrowUp, ArrowDown, FileSpreadsheet } from 'lucide-react';
import wmLogo from '../../assets/logos/WM-Logo-Full.png';
import wmLogoCb from '../../assets/WM-Logo-CB-Transparent.png';
import exportReportPdf from './export/reportPdfExporter';
import exportReportExcel from './export/reportExcelExporter';
import exportReportCsv from './export/reportCsvExporter';

// Canvas-based text measurement for fixed header column sizing
const _mc = document.createElement('canvas').getContext('2d');
const measureTextPx = (text, font) => { _mc.font = font; return _mc.measureText(String(text)).width; };

const ReportViewerPage = () => {
  const { reportSlug } = useParams();
  const { notify } = useNotifications();

  const [config, setConfig] = useState(null);
  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [executionMs, setExecutionMs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const [parameters, setParameters] = useState({});
  const [hasRun, setHasRun] = useState(false);

  // Sort state
  const [sortOrder, setSortOrder] = useState([]);
  const [defaultSort, setDefaultSort] = useState([]);
  const [isDefaultSort, setIsDefaultSort] = useState(true);

  // Pagination state — page size derived from physical PDF page dimensions
  const [currentPage, setCurrentPage] = useState(1);
  // eslint-disable-next-line no-unused-vars
  const [selectedPageSize, setSelectedPageSize] = useState(null); // null = use config default

  // Fixed pixel widths for parameter header columns — computed once from all possible values
  const [paramHeaderWidths, setParamHeaderWidths] = useState({});

  // Auto-scale table to fit viewport width
  const tableContainerRef = useRef(null);
  const tableRef = useRef(null);
  const [tableScale, setTableScale] = useState(1);

  // Load report config from API
  useEffect(() => {
    if (!reportSlug) return;

    const meta = getReportMeta(reportSlug);
    if (!meta) {
      notify.error(`Unknown report: ${reportSlug}`, 'error');
      setConfigLoading(false);
      return;
    }

    setConfigLoading(true);
    reportingApi.getConfig(meta.configId)
      .then(res => {
        setConfig(res.data);
        const initialParams = {};
        (res.data.parameters || []).forEach(p => {
          initialParams[p.field] = [];
        });
        setParameters(initialParams);

        const configSort = (res.data.sorting || []).map(s => ({
          field: s.field,
          direction: (s.direction || 'asc').toLowerCase()
        }));
        setDefaultSort(configSort);
        setSortOrder(configSort);
        setIsDefaultSort(true);
      })
      .catch(err => {
        notify.error(`Failed to load report config: ${err.message}`, 'error');
      })
      .finally(() => setConfigLoading(false));
  }, [reportSlug, notify]);

  // After config loads, fetch all param options and compute fixed column widths
  useEffect(() => {
    if (!config || !reportSlug) return;
    const params = config.parameters || [];
    if (params.length === 0) return;

    Promise.all(
      params.map(p =>
        reportingApi.getParameterOptions(reportSlug, p.field, null)
          .then(res => ({ field: p.field, label: p.label, options: res.data || [] }))
          .catch(() => ({ field: p.field, label: p.label, options: [] }))
      )
    ).then(results => {
      const widths = {};
      results.forEach(({ field, label, options }) => {
        const candidates = ['None Selected', label, ...options.map(o => String(o.fullDisplay || o.display || ''))];
        const maxW = candidates.reduce((max, text) => Math.max(max,
          measureTextPx(text, 'bold 6px sans-serif'),
          measureTextPx(text, '5px sans-serif')
        ), 0);
        widths[field] = Math.ceil(maxW) + 16;
      });
      setParamHeaderWidths(widths);
    });
  }, [config, reportSlug]);

  // Check if current sort matches default
  const checkIsDefault = useCallback((sort) => {
    if (sort.length !== defaultSort.length) return false;
    return sort.every((s, i) =>
      s.field === defaultSort[i].field && s.direction === defaultSort[i].direction
    );
  }, [defaultSort]);

  // Handle column header click for sorting
  const handleSortClick = useCallback((field, e) => {
    setSortOrder(prev => {
      let newSort;
      const existingIndex = prev.findIndex(s => s.field === field);

      if (e.shiftKey) {
        if (existingIndex >= 0) {
          newSort = [...prev];
          if (newSort[existingIndex].direction === 'asc') {
            newSort[existingIndex] = { field, direction: 'desc' };
          } else {
            newSort.splice(existingIndex, 1);
          }
        } else {
          newSort = [...prev, { field, direction: 'asc' }];
        }
      } else {
        if (existingIndex >= 0 && prev.length === 1) {
          if (prev[0].direction === 'asc') {
            newSort = [{ field, direction: 'desc' }];
          } else {
            newSort = [];
          }
        } else {
          newSort = [{ field, direction: 'asc' }];
        }
      }

      setIsDefaultSort(checkIsDefault(newSort));
      return newSort;
    });
    setCurrentPage(1);
  }, [checkIsDefault]);

  // Reset to default sort
  const resetSort = useCallback(() => {
    setSortOrder(defaultSort);
    setIsDefaultSort(true);
    setCurrentPage(1);
  }, [defaultSort]);

  // Sort data client-side
  const sortedData = useMemo(() => {
    if (sortOrder.length === 0) return data;

    return [...data].sort((a, b) => {
      for (const { field, direction } of sortOrder) {
        const camelField = field.charAt(0).toLowerCase() + field.slice(1);
        const displayKey = `${camelField}_Display`;
        const aVal = a[displayKey] != null ? a[displayKey] : a[camelField];
        const bVal = b[displayKey] != null ? b[displayKey] : b[camelField];

        if (aVal == null && bVal == null) continue;
        if (aVal == null) return direction === 'asc' ? 1 : -1;
        if (bVal == null) return direction === 'asc' ? -1 : 1;

        let cmp;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' });
        }

        if (cmp !== 0) return direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [data, sortOrder]);

  // Calculate rows per page from actual PDF layout measurements (points)
  const pageSize = useMemo(() => {
    if (!config?.layout) return 47;

    // Page dimensions in points (72pt = 1 inch), landscape orientation
    const pageDimensions = {
      letter:  { width: 792, height: 612 },   // 11x8.5
      tabloid: { width: 1224, height: 792 }    // 17x11
    };

    const dims = pageDimensions[config.layout.defaultPageSize] || pageDimensions.tabloid;

    // Fixed measurements matching ReportPdfStyles.js exactly
    const pageMarginTop = 30;
    const pageMarginBottom = 40;
    const headerHeight = 60;      // logo(24) + address(17) + padding/border/margin(19)
    const colHeaderHeight = 16;   // paddingV(8) + fontSize(6.5) + border(1.5)
    const fontSize = config.layout.fontSize || 8;
    const rowHeight = fontSize + 5 + 0.5;  // fontSize + paddingV(2.5×2) + border(0.5)

    const usableHeight = dims.height - pageMarginTop - pageMarginBottom - headerHeight - colHeaderHeight;
    const rowsPerPage = Math.floor(usableHeight / rowHeight);

    return Math.max(10, rowsPerPage);
  }, [config]);

  // Paginate sorted data
  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize]);

  // Run report
  const runReport = useCallback(async () => {
    if (!config) return;

    setLoading(true);
    try {
      const paramPayload = {};
      Object.entries(parameters).forEach(([field, selections]) => {
        const camelField = field.charAt(0).toLowerCase() + field.slice(1);
        if (selections && selections.length > 0) {
          paramPayload[camelField] = selections.map(s => ({
            operator: s.operator,
            value: s.value
          }));
        }
      });

      const res = await reportingApi.runReport(config.reportId, { parameters: paramPayload });
      setData(res.data.data);
      setTotalCount(res.data.totalCount);
      setExecutionMs(res.data.executionMs);
      setHasRun(true);
      setCurrentPage(1);
    } catch (err) {
      notify.error(`Report failed: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [config, parameters, notify]);

  // Recalculate scale when data or page changes
  useLayoutEffect(() => {
    const calcScale = () => {
      if (!tableContainerRef.current || !tableRef.current) return;
      setTableScale(1);
      requestAnimationFrame(() => {
        if (!tableContainerRef.current || !tableRef.current) return;
        const containerWidth = tableContainerRef.current.clientWidth;
        const tableWidth = tableRef.current.scrollWidth;
        if (tableWidth > containerWidth) {
          setTableScale(containerWidth / tableWidth);
        } else {
          setTableScale(1);
        }
      });
    };

    calcScale();
    window.addEventListener('resize', calcScale);
    return () => window.removeEventListener('resize', calcScale);
  }, [paginatedData, hasRun]);

  const updateParameter = (field, selections) => {
    setParameters(prev => ({ ...prev, [field]: selections }));
  };

  const activeFilterCount = Object.values(parameters).reduce((sum, s) => sum + (s?.length || 0), 0);
  const [exportProgress, setExportProgress] = useState(null);

  // Clear all filters across all parameters
  const clearAllFilters = useCallback(() => {
    const cleared = {};
    Object.keys(parameters).forEach(field => {
      cleared[field] = [];
    });
    setParameters(cleared);
  }, [parameters]);

  // PDF export
  const handleExportPdf = useCallback(async () => {
    if (!config || sortedData.length === 0) return;

    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    // Calculate proportional column widths as percentages for PDF
    const totalPx = cols.reduce((sum, c) => sum + (c.pixelWidth || c.width * 10 || 100), 0);
    const colWidths = {};
    cols.forEach(c => {
      const px = c.pixelWidth || c.width * 10 || 100;
      colWidths[c.field] = `${((px / totalPx) * 100).toFixed(1)}%`;
    });

    try {
      await exportReportPdf({
        config,
        columns: cols,
        columnWidths: colWidths,
        data: sortedData,
        parameters,
        logoSrc: wmLogo,
        onProgress: setExportProgress
      });
    } catch (err) {
      console.error('PDF export failed:', err);
      setExportProgress(null);
    }
  }, [config, sortedData, parameters]);

  // Excel export
  const handleExportExcel = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    try {
      await exportReportExcel({
        config,
        columns: cols,
        data: sortedData,
        parameters,
        onProgress: setExportProgress
      });
    } catch (err) {
      console.error('Excel export failed:', err);
      alert(`Excel export failed: ${err.message}\n\n${err.stack}`);
      setExportProgress(null);
    }
  }, [config, sortedData, parameters]);

  // CSV export
  const handleExportCsv = useCallback(async () => {
    if (!config || sortedData.length === 0) return;
    const cols = [...(config.columns || [])].sort((a, b) => a.order - b.order);
    try {
      await exportReportCsv({
        config,
        columns: cols,
        data: sortedData,
        onProgress: setExportProgress
      });
    } catch (err) {
      console.error('CSV export failed:', err);
      setExportProgress(null);
    }
  }, [config, sortedData]);

  if (configLoading) {
    return <div className="page-container"><p>Loading report configuration...</p></div>;
  }

  if (!config) {
    return <div className="page-container"><p>Report not found: {reportSlug}</p></div>;
  }

  const columns = [...(config.columns || [])].sort((a, b) => a.order - b.order);

  // Build sort lookup for column headers
  const sortLookup = {};
  sortOrder.forEach((s, i) => {
    sortLookup[s.field] = { direction: s.direction, priority: i + 1 };
  });

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      {/* Report Title — matches dev-module-header styling */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: '0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
          <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
            <FileSpreadsheet size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            {config.reportName}
          </h1>
          {config.description && (
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
              {config.description}
            </span>
          )}
        </div>
      </div>

      {/* ===== Section 1: Input Parameters ===== */}
      {config.parameters && config.parameters.length > 0 && (
        <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
          <div className="import-step-header">
            <span className="import-step-number">1</span>
            <h3>Input Parameters</h3>
          </div>
          <p className="import-step-desc">Select filter values to narrow report results.</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {config.parameters.map(param => {
              const sels = parameters[param.field] || [];
              return (
                <div key={param.field} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {/* Combobox input */}
                  <div style={{ flexShrink: 0 }}>
                    <ReportParameterCombobox
                      param={param}
                      selections={sels}
                      onChange={(selections) => updateParameter(param.field, selections)}
                      reportId={config.reportId}
                      activeFilters={parameters}
                    />
                  </div>
                  {/* Applied filters with remove button */}
                  {sels.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center', paddingTop: '20px' }}>
                      {sels.map((sel, i) => (
                        <span
                          key={i}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            padding: '2px 8px', borderRadius: '12px', fontSize: '12px',
                            background: sel.type === 'picked' ? '#e3edf7' : '#fef3cd',
                            color: sel.type === 'picked' ? '#1f497d' : '#856404',
                            border: `1px solid ${sel.type === 'picked' ? '#b8d4f0' : '#ffc107'}`
                          }}
                        >
                          <span style={{
                            fontSize: '9px', fontWeight: 700,
                            background: sel.type === 'picked' ? '#1f497d' : '#856404',
                            color: '#fff', borderRadius: '50%',
                            width: '14px', height: '14px', display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', flexShrink: 0
                          }}>
                            {i + 1}
                          </span>
                          {sel.display}
                          <span
                            onClick={() => {
                              const updated = sels.filter((_, idx) => idx !== i);
                              updateParameter(param.field, updated);
                            }}
                            style={{
                              cursor: 'pointer', fontWeight: 'bold', fontSize: '14px',
                              lineHeight: 1, marginLeft: '2px', opacity: 0.7,
                              color: sel.type === 'picked' ? '#1f497d' : '#856404'
                            }}
                            onMouseEnter={(e) => e.target.style.opacity = 1}
                            onMouseLeave={(e) => e.target.style.opacity = 0.7}
                          >
                            x
                          </span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ===== Section 2: Formatting/Exporting Parameters ===== */}
      <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
        <div className="import-step-header">
          <span className="import-step-number">2</span>
          <h3>Formatting / Exporting Parameters</h3>
        </div>
        <p className="import-step-desc">Run report, adjust sorting, and export results.</p>
        <ReportToolbar
          onRunReport={runReport}
          loading={loading}
          activeFilterCount={activeFilterCount}
          onClearFilters={clearAllFilters}
          isDefaultSort={isDefaultSort}
          sortOrder={sortOrder}
          defaultSort={defaultSort}
          onResetSort={resetSort}
          totalCount={totalCount}
          executionMs={executionMs}
          pageSize={pageSize}
          pageSizeLabel={config?.layout?.defaultPageSize || 'tabloid'}
          totalPages={totalPages}
          hasRun={hasRun}
          onExportPdf={hasRun && sortedData.length > 0 ? handleExportPdf : null}
          onExportExcel={hasRun && sortedData.length > 0 ? handleExportExcel : null}
          onExportCsv={hasRun && sortedData.length > 0 ? handleExportCsv : null}
          exportProgress={exportProgress}
        />
      </div>

      {/* ===== Section 3: Report Preview ===== */}
      <div className="import-step-panel" style={{ marginTop: 'var(--spacing-lg)' }}>
        <div className="import-step-header">
          <span className="import-step-number">3</span>
          <h3>Report Preview</h3>
        </div>
        <p className="import-step-desc">Preview matches the PDF export layout. Click column headers to sort. Shift+click for multi-column sort.</p>

      {/* Report Page Header — fixed height, mirrors PDF output */}
      <div style={{
        display: 'flex', alignItems: 'flex-start',
        minHeight: '115px',
        padding: '10px 16px',
        border: '1px solid var(--color-gray-300)',
        borderBottom: '2px solid var(--color-primary)',
        background: '#fff', marginBottom: '0', borderRadius: '4px 4px 0 0'
      }}>
        {/* Left: Logo + Address */}
        <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
          <img
            src={wmLogo}
            alt="Wunderlich-Malec"
            style={{ height: '40px', width: 'auto' }}
          />
          <div style={{ fontSize: '9px', color: 'var(--color-gray-500)', lineHeight: 1.4 }}>
            6101 Blue Circle Dr. Eden Prairie, MN 55343<br />
            Tel: 952-933-3222 | Fax: 952-933-0608
          </div>
        </div>

        {/* Center: Report Title */}
        <div style={{ flex: '1 1 auto', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{
            fontSize: '16px', fontWeight: 700, color: config?.branding?.titleColor || '#1f497d',
            fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
            textTransform: 'uppercase', letterSpacing: '1px'
          }}>
            {config.reportName}
          </div>
        </div>

        {/* Right: Selected parameters — 2-row grid, col widths from row-1 params.
            Row-1 cells have minHeight for 4 values so Parent/Panel never move. */}
        {(() => {
          const params = config.parameters || [];
          const col1W = paramHeaderWidths[params[0]?.field] || 80;
          const col2W = paramHeaderWidths[params[1]?.field] || 80;
          return (
            <div style={{
              flex: '0 0 auto',
              display: 'grid',
              gridTemplateColumns: `${col1W}px ${col2W}px`,
              columnGap: '8px',
              rowGap: '4px',
              alignItems: 'start'
            }}>
              {params.map((param, paramIdx) => {
                const fixedW = paramHeaderWidths[param.field] || 80;
                const selections_list = parameters[param.field] || [];
                return (
                  <div key={param.field} style={{ width: `${fixedW}px`, minHeight: paramIdx < 2 ? '43px' : undefined }}>
                    <div style={{
                      fontSize: '6px', fontWeight: 700, color: 'var(--color-gray-700)',
                      borderBottom: '1px solid var(--color-gray-300)',
                      paddingBottom: '2px', marginBottom: '3px'
                    }}>
                      {param.label}
                    </div>
                    {selections_list.length === 0 ? (
                      <div style={{ fontSize: '5px', color: 'var(--color-gray-400)', fontStyle: 'italic' }}>
                        None Selected
                      </div>
                    ) : (
                      selections_list.map((sel, i) => (
                        <div key={i} style={{ fontSize: '5px', color: 'var(--color-gray-600)', lineHeight: 1.5 }}>
                          {sel.fullDisplay || sel.display}
                        </div>
                      ))
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Data Table */}
      {hasRun && (
        <div
          ref={tableContainerRef}
          className="data-grid-container"
          style={{ flex: 'none', maxHeight: '60vh', overflowY: 'auto', overflowX: 'hidden' }}
        >
          <table
            ref={tableRef}
            className="data-grid"
            style={{
              borderCollapse: 'collapse',
              transform: `scale(${tableScale})`,
              transformOrigin: 'top left',
              width: tableScale < 1 ? `${100 / tableScale}%` : '100%',
              marginBottom: tableScale < 1 ? `${(tableScale - 1) * (tableRef.current?.offsetHeight ?? 0)}px` : 0
            }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr>
                {columns.map(col => {
                  const sortInfo = sortLookup[col.field];
                  return (
                    <th
                      key={col.field}
                      onClick={(e) => handleSortClick(col.field, e)}
                      style={{
                        minWidth: `${col.pixelWidth || col.width * 10 || 80}px`,
                        textAlign: col.align || 'left',
                        padding: 'var(--spacing-sm) var(--spacing-md)',
                        borderBottom: '2px solid var(--color-gray-300)',
                        borderRight: '1px solid var(--color-gray-300)',
                        backgroundColor: 'var(--color-gray-200)',
                        fontWeight: 600,
                        color: 'var(--color-gray-700)',
                        fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                        textTransform: 'uppercase',
                        fontSize: 'var(--text-xs)',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                        userSelect: 'none',
                        verticalAlign: 'bottom'
                      }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {col.label}
                        {sortInfo && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
                            {sortInfo.direction === 'asc'
                              ? <ArrowUp size={11} style={{ opacity: 0.8 }} />
                              : <ArrowDown size={11} style={{ opacity: 0.8 }} />
                            }
                            {sortOrder.length > 1 && (
                              <span style={{
                                fontSize: '9px', fontWeight: 700,
                                background: 'var(--color-primary-light)', color: 'var(--color-primary)',
                                borderRadius: '50%',
                                width: '14px', height: '14px', display: 'inline-flex',
                                alignItems: 'center', justifyContent: 'center'
                              }}>
                                {sortInfo.priority}
                              </span>
                            )}
                          </span>
                        )}
                      </span>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} style={{
                    padding: 'var(--spacing-xl)', textAlign: 'center',
                    color: 'var(--color-gray-400)', fontSize: 'var(--text-sm)'
                  }}>
                    No data returned
                  </td>
                </tr>
              ) : (
                paginatedData.map((row, idx) => (
                  <tr
                    key={row.id || idx}
                    style={{
                      backgroundColor: config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--color-teal-50)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor =
                      config.layout?.zebraStriping && idx % 2 === 1
                        ? 'var(--color-gray-100)' : 'var(--color-bg-white)'}
                  >
                    {columns.map(col => {
                      const camelField = col.field.charAt(0).toLowerCase() + col.field.slice(1);
                      const displayKey = `${camelField}_Display`;
                      const displayValue = row[displayKey] != null ? row[displayKey] : row[camelField];

                      return (
                        <td key={col.field} style={{
                          padding: 'var(--spacing-sm) var(--spacing-md)',
                          textAlign: col.align || 'left',
                          borderBottom: '1px solid var(--color-gray-200)',
                          borderRight: '1px solid var(--color-gray-200)',
                          fontSize: 'var(--text-sm)',
                          verticalAlign: 'middle',
                          whiteSpace: 'nowrap'
                        }}>
                          {displayValue != null ? String(displayValue) : ''}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Paginator */}
      {hasRun && (
        <ReportPaginator
          currentPage={currentPage}
          totalPages={totalPages}
          totalRows={sortedData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Report Preview Footer — mirrors PDF footer */}
      {reportSlug === 'io-assignments' && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 16px',
          borderTop: '1px solid var(--color-gray-300)',
          background: '#fff',
          fontSize: '9px', color: 'var(--color-gray-500)',
          borderRadius: '0 0 4px 4px'
        }}>
          <span>{new Date().toLocaleString()}</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={wmLogoCb} alt="Wunderlich-Malec" style={{ height: '20px', width: 'auto' }} />
            Wunderlich-Malec Confidential
            <img src={wmLogoCb} alt="Wunderlich-Malec" style={{ height: '20px', width: 'auto' }} />
          </span>
          <span>{hasRun ? `Page ${currentPage} of ${totalPages}` : ''}</span>
        </div>
      )}

      {/* No report run yet */}
      {!hasRun && !loading && (
        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#999' }}>
          <p style={{ fontSize: '16px' }}>Set parameters and click <strong>Run Report</strong> to view data.</p>
        </div>
      )}
      </div>{/* close Section 3 import-step-panel */}
    </div>
  );
};

export default ReportViewerPage;
